import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { isEmpty } from 'lodash-es';

import { ChangeUserDetailsRequest } from '@vi-iot/client';

import { PATTERN } from '@common';
import { NO_VALUE } from '@pipes';

import { SupportedCountry } from '../../../../shared/models/supportedCountry.model';
import { SupportedCountriesService } from '../../../../shared/services/supported-countries.service';
import { AddressDetailsFormGroup } from './address-details.helper';

@Component({
  selector: 'app-address-details',
  templateUrl: './address-details.component.html',
  styleUrls: ['./address-details.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AddressDetailsComponent implements OnInit {
  formGroup: FormGroup<AddressDetailsFormGroup>;

  @Input() details: {
    street: string;
    houseNumber: string;
    zip: string;
    city: string;
    country: string;
  };
  supportedCountries: SupportedCountry[];
  selectedCountry: SupportedCountry;

  @Output() cancel: EventEmitter<void> = new EventEmitter<void>();

  @Output() save: EventEmitter<ChangeUserDetailsRequest> = new EventEmitter<
    ChangeUserDetailsRequest
  >();

  readonly NO_VALUE = NO_VALUE;

  constructor(private supportedCountriesService: SupportedCountriesService) {}

  ngOnInit(): void {
    this.supportedCountries = this.supportedCountriesService.getUniqueSupportedCountriesSortedAlphabetically();
    const country = this.parseCountryCode(this.details.country);
    this.formGroup = new FormGroup<AddressDetailsFormGroup>({
      street: new FormControl<string>(this.details.street, [Validators.pattern(PATTERN.NO_SPACES)]),
      houseNumber: new FormControl<string>(this.details.houseNumber, [
        Validators.pattern(PATTERN.NO_SPACES),
      ]),
      zip: new FormControl<string>(this.details.zip, [
        Validators.required,
        Validators.pattern(PATTERN.NO_SPACES),
      ]),
      city: new FormControl<string>(this.details.city, [
        Validators.required,
        Validators.pattern(PATTERN.NO_SPACES),
      ]),
      country: new FormControl<string>(country, [Validators.required]),
    });
    this.updateSelectedCountry();
  }

  onCancel(): void {
    this.cancel.emit();
  }

  onSave(): void {
    const value = this.formGroup.value;
    const payload: ChangeUserDetailsRequest = {
      address: {
        countryCode: value.country?.trim(),
        city: value.city?.trim(),
        postalCode: value.zip?.trim(),
        street: value.street?.trim(),
        houseNumber: value.houseNumber?.trim(),
      },
    };

    if (isEmpty(payload)) {
      this.onCancel();
    } else {
      this.save.emit(payload);
    }
  }

  private parseCountryCode(code: string): string {
    if (code && code !== '-') {
      const parsed = code.split('-');

      return parsed[0].toLowerCase();
    }

    return '';
  }

  updateSelectedCountry(): void {
    const countryCode = this.formGroup.controls['country'].value;

    this.selectedCountry = this.supportedCountries.find(
      (country: SupportedCountry) => country.countryCode.toLowerCase() === countryCode.toLowerCase()
    );
  }
}
